/**
 * TanStack Query hooks for the Banners admin feature.
 *
 * The list/create/update/delete endpoints are typed by the generated OpenAPI
 * surface. The reorder endpoint isn't published in `api.d.ts` yet, so we cast
 * the path to `as never` and rely on the same `api` client (auth + retry
 * middleware applied) — TODO: drop the cast once the OpenAPI publishes
 * `POST /api/v1/admin/banners/reorder`.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';
import type { components } from '@favelafood/shared/api/generated/api';

export type BannerResponse = components['schemas']['BannerResponse'];
export type CreateBannerRequest = components['schemas']['CreateBannerRequest'];
export type UpdateBannerRequest = components['schemas']['UpdateBannerRequest'];
export type CouponResponse = components['schemas']['CouponResponse'];

export const bannersKeys = {
  all: ['banners'] as const,
  list: () => [...bannersKeys.all, 'list'] as const,
};

export const couponsKeys = {
  all: ['coupons'] as const,
  list: () => [...couponsKeys.all, 'list'] as const,
};

export function useBannersList(): UseQueryResult<BannerResponse[]> {
  return useQuery({
    queryKey: bannersKeys.list(),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/admin/banners');
      if (error) throw error as Error;
      return data ?? [];
    },
  });
}

export function useCouponsList(): UseQueryResult<CouponResponse[]> {
  return useQuery({
    queryKey: couponsKeys.list(),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/admin/coupons');
      if (error) throw error as Error;
      return data ?? [];
    },
  });
}

export function useCreateBanner(): UseMutationResult<
  BannerResponse,
  Error,
  CreateBannerRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const { data, error } = await api.POST('/api/v1/admin/banners', { body });
      if (error) throw error as Error;
      return data as BannerResponse;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bannersKeys.all });
    },
  });
}

export function useUpdateBanner(): UseMutationResult<
  BannerResponse,
  Error,
  { id: string; body: UpdateBannerRequest }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) => {
      const { data, error } = await api.PUT('/api/v1/admin/banners/{id}', {
        params: { path: { id } },
        body,
      });
      if (error) throw error as Error;
      return data as BannerResponse;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bannersKeys.all });
    },
  });
}

export function useDeleteBanner(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { error } = await api.DELETE('/api/v1/admin/banners/{id}', {
        params: { path: { id } },
      });
      if (error) throw error as Error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bannersKeys.all });
    },
  });
}

/**
 * Reorder mutation. Endpoint not published in the generated OpenAPI yet —
 * `as never` cast is the agreed-upon escape hatch (see TASK-030 brief).
 * TODO: remove cast once the backend exposes the reorder paths.
 */
export function useReorderBanners(): UseMutationResult<void, Error, { ids: string[] }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids }) => {
      const { error } = await api.POST(
        '/api/v1/admin/banners/reorder' as never,
        { body: { ids } } as never,
      );
      if (error) throw error as Error;
    },
    onError: () => {
      // refetch reverts the optimistic UI on failure
      void queryClient.invalidateQueries({ queryKey: bannersKeys.all });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bannersKeys.all });
    },
  });
}
